import type { ChestSlot, ChestTier } from '../core/types';

const TIER_ICONS: Record<ChestTier, string> = {
  bronze: '/assets/ui/icon_chest_bronze.png',
  silver: '/assets/ui/icon_chest_silver.png',
  gold: '/assets/ui/icon_chest_gold.png',
  royal: '/assets/ui/icon_chest_royal.png',
};

const TIER_LABELS: Record<ChestTier, string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  royal: 'Royal',
};

const TIER_COLORS: Record<ChestTier, string> = {
  bronze: '#cd7f32',
  silver: '#94a3b8',
  gold: '#f59e0b',
  royal: '#a78bfa',
};

const UNLOCK_HOURS: Record<ChestTier, string> = {
  bronze: '1H',
  silver: '4H',
  gold: '8H',
  royal: '12H',
};

function formatCountdown(ms: number): string {
  if (ms <= 0) return '0:00';
  const totalSec = Math.ceil(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function getUnlockDuration(tier: ChestTier): number {
  switch (tier) {
    case 'bronze': return 1 * 60 * 60 * 1000;
    case 'silver': return 4 * 60 * 60 * 1000;
    case 'gold':   return 8 * 60 * 60 * 1000;
    case 'royal':  return 12 * 60 * 60 * 1000;
  }
}

export interface ChestSlotCallbacks {
  onTapLocked?: (index: number) => void;
  onTapUnlocking?: (index: number) => void;
  onTapReady?: (index: number) => void;
  getRushCost?: (index: number) => number;
}

export function renderChestSlots(
  container: HTMLElement,
  slots: ChestSlot[],
  callbacks?: ChestSlotCallbacks
): void {
  container.innerHTML = '';

  for (let i = 0; i < 4; i++) {
    const slot = slots[i] ?? { state: 'empty', tier: null, unlockEndTime: null };
    const el = document.createElement('div');
    el.className = 'chest-slot';

    const tierClass = slot.tier ? `chest-tier-${slot.tier}` : '';

    switch (slot.state) {
      case 'empty':
        el.classList.add('chest-slot-empty');
        el.innerHTML = `
          <div class="chest-card-inner">
            <span class="chest-empty-icon">+</span>
          </div>
        `;
        break;

      case 'locked': {
        el.classList.add('chest-slot-locked', tierClass);
        const tier = slot.tier!;
        el.innerHTML = `
          <div class="chest-card-inner">
            <div class="chest-status-label">Locked</div>
            <img class="chest-icon" src="${TIER_ICONS[tier]}" alt="${TIER_LABELS[tier]}">
            <div class="chest-unlock-time">${UNLOCK_HOURS[tier]}</div>
          </div>
        `;
        el.addEventListener('click', () => callbacks?.onTapLocked?.(i));
        break;
      }

      case 'unlocking': {
        el.classList.add('chest-slot-unlocking', tierClass);
        const tier = slot.tier!;
        const remaining = slot.unlockEndTime ? Math.max(0, slot.unlockEndTime - Date.now()) : 0;
        const total = getUnlockDuration(tier);
        const progress = Math.min(100, ((total - remaining) / total) * 100);
        el.innerHTML = `
          <div class="chest-card-inner">
            <div class="chest-status-label chest-status-timer" data-end="${slot.unlockEndTime}">${formatCountdown(remaining)}</div>
            <img class="chest-icon" src="${TIER_ICONS[tier]}" alt="${TIER_LABELS[tier]}">
            <div class="chest-open-action">Open now<br><span class="chest-gem-cost">🪙 ${callbacks?.getRushCost?.(i) ?? 0}</span></div>
            <div class="chest-progress-bar"><div class="chest-progress-fill" style="width: ${progress}%"></div></div>
          </div>
        `;
        el.addEventListener('click', () => callbacks?.onTapUnlocking?.(i));
        break;
      }

      case 'ready': {
        el.classList.add('chest-slot-ready', tierClass);
        const tier = slot.tier!;
        el.innerHTML = `
          <div class="chest-card-inner">
            <div class="chest-status-label chest-status-ready">Open!</div>
            <img class="chest-icon chest-icon-ready" src="${TIER_ICONS[tier]}" alt="${TIER_LABELS[tier]}">
          </div>
        `;
        el.addEventListener('click', () => callbacks?.onTapReady?.(i));
        break;
      }
    }

    if (slot.tier) {
      el.style.setProperty('--chest-color', TIER_COLORS[slot.tier]);
      el.title = `${TIER_LABELS[slot.tier]} Chest`;
    }

    container.appendChild(el);
  }
}

export function updateChestTimers(container: HTMLElement): boolean {
  let anyReady = false;
  const timerElements = container.querySelectorAll('.chest-status-timer');

  timerElements.forEach((el) => {
    const endTime = Number(el.getAttribute('data-end'));
    if (!endTime) return;

    const remaining = Math.max(0, endTime - Date.now());
    el.textContent = formatCountdown(remaining);

    if (remaining <= 0) {
      anyReady = true;
    }

    const slot = el.closest('.chest-slot') as HTMLElement;
    const fill = slot?.querySelector('.chest-progress-fill') as HTMLElement;
    if (fill) {
      const tierEl = slot.querySelector('.chest-icon') as HTMLImageElement;
      const src = tierEl?.src ?? '';
      let total = 3600000;
      if (src.includes('silver')) total = 4 * 3600000;
      else if (src.includes('gold')) total = 8 * 3600000;
      else if (src.includes('royal')) total = 12 * 3600000;
      const progress = Math.min(100, ((total - remaining) / total) * 100);
      fill.style.width = `${progress}%`;
    }
  });

  return anyReady;
}
